import { TRIFOLD_FOLD_LINE, pageFolds, parseFoldLayout } from '@messenger/utils'
import type { FoldLayoutSource, FoldMargin } from '@messenger/utils'
import type { EditorOrderPageDetailsNormalized } from '@messenger/types'

type Layout = EditorOrderPageDetailsNormalized['layout']

export type LayoutMargins = Pick<
  Layout,
  | 'margin_top'
  | 'margin_right'
  | 'margin_bottom'
  | 'margin_left'
  | 'margin_center_horizontal'
  | 'margin_center_vertical'
>

/**
 * A product layout's margins as the editor, the preview and the PDF read
 * them: an empty edge margin is 0 and an empty center margin is null. No
 * layout is no margins.
 *
 * A trifold has no center margins here, whatever it stores: its folds are
 * off the page middle, so a stored center margin must neither warn nor bound
 * Lotus growth at the middle. What the editor draws for a trifold comes from
 * `pageCenterAndFoldMarginsOf`.
 */
export const layoutMarginsOf = (
  layout:
    | (Partial<LayoutMargins> & { fold_line?: string | null })
    | null
    | undefined,
): LayoutMargins | null => {
  if (!layout) return null
  const isTrifold = layout.fold_line === TRIFOLD_FOLD_LINE
  return {
    margin_top: layout.margin_top || 0,
    margin_right: layout.margin_right || 0,
    margin_bottom: layout.margin_bottom || 0,
    margin_left: layout.margin_left || 0,
    margin_center_horizontal: isTrifold
      ? null
      : layout.margin_center_horizontal || null,
    margin_center_vertical: isTrifold
      ? null
      : layout.margin_center_vertical || null,
  }
}

export type PageCenterAndFoldMargins = {
  marginCenterHorizontal: number | null | undefined
  marginCenterVertical: number | null | undefined
  /** A trifold page's fold margins; null for any other product. */
  foldMargins: FoldMargin[] | null
}

/**
 * What the editor draws across a page's middle and folds. A trifold, valid
 * or not, draws a fold margin at each of this page's folds (none when its
 * panel widths are invalid) and no center margin. Every other product draws
 * its stored center margins.
 */
export const pageCenterAndFoldMarginsOf = (
  layout: FoldLayoutSource | null | undefined,
  pageNumber: number | null | undefined,
  sheetWidth: number | null | undefined,
): PageCenterAndFoldMargins => {
  const folds = pageFolds(
    parseFoldLayout(layout),
    pageNumber ?? 0,
    sheetWidth ?? 0,
  )
  if (folds.kind === 'not-trifold') {
    return {
      marginCenterHorizontal: layout?.margin_center_horizontal,
      marginCenterVertical: layout?.margin_center_vertical,
      foldMargins: null,
    }
  }
  return {
    marginCenterHorizontal: null,
    marginCenterVertical: null,
    foldMargins: folds.foldMargins,
  }
}
